/**
 * WorkAxis — shared site content (services-first positioning).
 */

export const serviceBuckets = [
    {
        name: "AI Automation",
        tagline: "Replace repetitive manual work with intelligent workflows.",
        desc: "For teams drowning in repetitive tasks — we build AI agents, document pipelines, and integrations that run in production.",
        examples: [
            "AI agents and internal copilots",
            "RAG / knowledge assistants",
            "WhatsApp AI workflows",
            "Document processing",
            "Customer support automation"
        ],
        points: [
            "Map manual steps and automate what humans shouldn't repeat",
            "LLM and RAG integrations tied to your data and tools",
            "WhatsApp, email, and CRM handoffs with human-in-the-loop",
            "Pilot one workflow, then expand with confidence"
        ]
    },
    {
        name: "Custom Business Software",
        tagline: "When spreadsheets and off-the-shelf tools stop scaling.",
        desc: "CRM, portals, dashboards, and workflow systems built around how your business actually operates.",
        examples: [
            "CRM and pipeline tools",
            "Customer and admin portals",
            "Approval and document workflows",
            "Booking and scheduling systems",
            "Internal operations platforms"
        ],
        points: [
            "Designed for your process — not forced into a template",
            "Integrations with existing tools and data sources",
            "Role-based access, audit trails, and reporting",
            "AntCRM is our proof: we build and run this category ourselves"
        ]
    },
    {
        name: "Product Engineering",
        tagline: "Your product engineering partner — without hiring a full team.",
        desc: "Startups and product companies get dedicated capacity for MVP through scale: backend, APIs, mobile, cloud, QA, and DevOps.",
        examples: [
            "MVP to production",
            "API and backend engineering",
            "Web and mobile applications",
            "AI feature integration",
            "Cloud architecture and DevOps"
        ],
        points: [
            "End-to-end ownership of modules or entire products",
            "Modern stack: React, Python/FastAPI, Node, AWS, PostgreSQL",
            "Automated testing, CI/CD, and performance at scale",
            "Same discipline we use on Pixis, Realaml, and AntCRM"
        ]
    },
    {
        name: "AI Transformation",
        tagline: "Find where AI reduces cost — before you build the wrong thing.",
        desc: "We map workflows, identify automation opportunities, and design solutions that plug into your existing stack.",
        examples: [
            "Workflow and process mapping",
            "Automation roadmap and architecture",
            "ROI-focused pilot scoping",
            "Integration with SAP, CRM, and internal systems",
            "Ongoing optimization and support"
        ],
        points: [
            "Start with the business problem, not a generic AI demo",
            "Clear scope, timeline, and fixed commercial proposal after discovery",
            "Build only what moves the needle on manual cost and errors",
            "Hand off to your team or stay on as engineering partner"
        ]
    }
];

export const idealCustomers = [
    {
        name: "SaaS & product companies",
        message: "Need engineering capacity without hiring an entire team?",
        signals: [
            "Backlog growing faster than your team can ship",
            "Need AI, integrations, or platform hardening",
            "Want a partner who thinks in products, not tickets"
        ]
    },
    {
        name: "SMEs with manual operations",
        message: "Still running leads, approvals, or documents by email and spreadsheets?",
        signals: [
            "Too many handoffs between people and tools",
            "No single view of customers or operations",
            "Want fewer mistakes and faster response times"
        ]
    },
    {
        name: "Startups",
        message: "Have an idea but don't want twelve months building a team first?",
        signals: [
            "Idea → MVP → production on one partner",
            "Architecture and stack choices that won't need a rewrite",
            "Path to scale when traction hits"
        ]
    }
];

/** Homepage hero — copy, workflow data, and project proof cards. */
export const heroContent = {
    eyebrow: "AI + Product Engineering",
    headline: "From business problems to production-ready software.",
    headlineAccent: "production-ready",
    description:
        "WorkAxis is an AI and product engineering company with 5+ years of experience building production systems. " +
        "We help businesses design, build and operate SaaS products, AI agents, business automation and custom software.",
    trustLine: "5+ years · Production systems · AI · Product engineering",
    primaryCta: "Discuss Your Project",
    secondaryCta: "See Our Work"
};

export const heroCapabilities = [
    "AI Agents",
    "Business Automation",
    "SaaS",
    "Product Engineering",
    "Integrations"
];

/** Hero visualization — product birth story (abstract, no client assets). */
export const heroProductPhases = [
    {id: "idea", label: "Idea"},
    {id: "build", label: "Build"},
    {id: "intelligence", label: "Intelligence"},
    {id: "connected", label: "Connected"},
    {id: "production", label: "Live"}
];

export const heroMobileStory = ["Idea", "Product", "Live"];

export const heroConnectionNodes = ["Data", "API", "Payments", "Messaging", "Cloud", "Users"];

/** Phase hold times — Idea is shorter, Live holds longest; ~11.5s full loop at default. */
export const heroPhaseMs = 2200;
export const heroIdeaPhaseMs = 1700;
export const heroLivePhaseMs = 3200;

export const howWeWork = [
    {
        step: "Discover",
        desc: "We understand your business, workflow, and technical requirements."
    },
    {
        step: "Design",
        desc: "We define the architecture, solution, and implementation plan."
    },
    {
        step: "Build",
        desc: "Our engineering team develops and integrates the solution."
    },
    {
        step: "Scale",
        desc: "We provide ongoing engineering, AI optimization, and support."
    }
];

export const howWeWorkNote =
    "Every project is different. After understanding your requirements, we provide a clear scope, timeline, and fixed commercial proposal.";

export const typicalEngagements = [
    {
        name: "AI & Automation",
        desc: "Projects typically start with a focused discovery and pilot before moving to larger implementation."
    },
    {
        name: "Product Engineering",
        desc: "Available as project-based delivery or dedicated monthly engineering capacity."
    },
    {
        name: "Custom Software",
        desc: "Scoped and priced based on functionality, integrations, and complexity."
    }
];

export const techStack = [
    "Python",
    "FastAPI",
    "React",
    "Node.js",
    "AWS",
    "PostgreSQL",
    "LLMs & RAG",
    "WhatsApp APIs",
    "Firebase",
    "Elasticsearch"
];

export const workFlow = [
    "Workflow & discovery",
    "Architecture & design",
    "Prototyping",
    "Development",
    "Quality assurance",
    "Integration & launch",
    "Deploy & monitor",
    "Operate & improve"
];

export const antcrmFeatures = [
    {
        name: "Lead Capture",
        desc: "Embeddable forms and widgets that capture leads from any website, with full source analytics."
    },
    {
        name: "Scheduling",
        desc: "Built-in booking pages let prospects schedule meetings with your team in one click."
    },
    {
        name: "Pipeline Management",
        desc: "Track every lead from first touch to closed deal with clear, configurable stages."
    },
    {
        name: "Automation",
        desc: "Automated follow-ups and reminders so no opportunity slips through the cracks."
    }
];

export const antcrmProofPoints = [
    "Product architecture, UX, APIs, and cloud deployments",
    "Billing, integrations, customer support, and scaling in production",
    "We eat our own dog food — AntCRM is how we run our own sales"
];

/** Wilo — our in-house browser extension (wilotab.com). */
export const wiloProduct = {
    name: "Wilo",
    url: "https://wilotab.com",
    domain: "wilotab.com",
    tag: "Our product · Browser extension",
    tagline: "The tab manager that organizes itself.",
    lead: "A Chromium extension for Chrome, Edge, and Brave that files your open tabs into projects as you " +
        "browse, so your tabs end up in a calmer place. We designed, built, and ship it in-house.",
    proofPoints: [
        "Auto-organizes tabs into projects from titles, addresses, and the tab that opened them",
        "Private by design: organizing runs on your device and reads only titles and URLs",
        "Live with free and paid plans, built and run in-house"
    ],
    features: [
        {
            name: "Auto-organize",
            desc: "Tabs are filed into projects as you browse, with no manual grouping."
        },
        {
            name: "Tuck idle tabs",
            desc: "Past a limit, idle tabs are tucked away without being closed, so they stay out of the way and are never lost."
        },
        {
            name: "One-click switch",
            desc: "Hide one project's tabs and restore another's in a single click."
        },
        {
            name: "⌘K search",
            desc: "Instant search across tabs, collections, notes, and links, even offline."
        }
    ]
};

export const clientWork = [
    {
        name: "Pixis — Creative AI (Adroom)",
        image: "/images/pixis-creative-ai.png",
        link: "https://pixis.ai/products/creative-ai/",
        problem: "Adroom needed a reliable Analyze module inside a fast-moving generative AI product — with clear ownership and predictable delivery.",
        built: "End-to-end engineering of the complete Analyze module within Pixis Creative AI.",
        tech: ["React", "Python", "AWS", "Analytics pipelines"],
        outcome: "Shipped on committed timelines with full module ownership — praised by product leadership for punctuality and quality.",
        ownership: "End-to-end ownership of the Analyze module"
    },
    {
        name: "Realaml",
        image: "/images/realaml-dashboard.png",
        link: "https://realaml.com/",
        problem: "Realaml needed e-sign capabilities integrated into their AML and customer onboarding platform for compliance workflows.",
        built: "E-sign module and integration into Realaml's existing product stack.",
        tech: ["Web platform", "Document workflows", "Integrations"],
        outcome: "Production-ready e-sign flow for onboarding and compliance use cases.",
        ownership: "E-sign development and integration"
    },
    {
        name: "Baylyn CRM",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Products/baylyncrm_d6_ZVN-IH.webp",
        link: "https://www.baylyncrm.com/",
        problem: "Marketing and sales teams needed one place for customer information and documents instead of scattered files and tools.",
        built: "CRM platform for customer data, documents, and business-user workflows.",
        tech: ["Web application", "CRM", "Document management"],
        outcome: "Simplified day-to-day work for business users managing clients and collateral.",
        ownership: "Platform engineering and product delivery"
    }
];

export const clients = [
    {
        name: "Dun & Bradstreet",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Clients/logo-dnb-gray_YuSKpyFxB.png"
    },
    {
        name: "Pixis",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Clients/pixis_white_gray_nVg97pw3k.png"
    },
    {
        name: "Realaml",
        image: "/images/realaml-logo.svg"
    },
    {
        name: "Baylyn Media",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Clients/Baylyn_Media_gray_wHCEXujUG.png"
    }
];

export const testimony = [
    {
        name: "Shivam Sharma",
        designation: "Product Head — Pixis",
        command: "Thank you for taking the ownership of complete Analyze Module. You have been quite punctual with your timelines and it's been a great pleasure working with you."
    },
    {
        name: "Lauren Moore",
        designation: "Client",
        command: "Great provider! Shyamsundar was a great communicator and also very experienced, and he did the job quickly and well. He caught on really fast and was able to produce good quality code without too much direction. A pleasure to work with — I would absolutely hire him again."
    },
    {
        name: "Rishab",
        designation: "Director — Requity Applied Sciences",
        command: "It was great working with Shyam. He is an excellent developer, he exceeded my expectations."
    },
    {
        name: "Pugal Ramasamy",
        designation: "Lead Analyst — Dun & Bradstreet",
        command: "It was a good working experience with WorkAxis. Shyam and Padma have delivered the project on the committed timeframe with expected quality. I really appreciate your work and effort."
    }
];

export const ourTeam = [
    {
        name: "Chitradevi",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Team/woman_3-nR4b1cT.jpeg",
        role: "CEO",
        link: "https://www.linkedin.com/in/chitradevi-balasubramani-091962128/"
    },
    {
        name: "Shyam Sundar",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Team/boy_Xuam6LniN.jpeg",
        role: "CTO",
        link: "https://www.linkedin.com/in/shyamsundar-thandalamganesan-1b203313b/"
    },
    {
        name: "Padma Dev E",
        image: "https://ik.imagekit.io/kittydev/Workaxis/Team/boy_Xuam6LniN.jpeg",
        role: "SDE",
        link: "https://www.linkedin.com/in/padmadev/"
    }
];

export const values = [
    {
        name: "Ownership",
        desc: "We take complete responsibility for what we build — from the first requirement to production support."
    },
    {
        name: "Simplicity",
        desc: "Complex business problems have simple, well-designed solutions. We find them."
    },
    {
        name: "Craft",
        desc: "Premium design and engineering, always on time and on budget. Our work is second to none."
    }
];

export const contactInterests = [
    "AI Automation",
    "Custom Business Software",
    "Product Engineering",
    "AI Transformation",
    "Not sure yet"
];
